"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, Sparkles, TrendingUp, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const challenges = [{
  id: 1,
  title: "Pilgrimage Peaks & Compression",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Manage extreme spikes during Janmashtami, Holi, and Kartik Maas alongside mid-week pilgrimage dips. We implement dynamic minimum stay restrictions and pace-based pricing to prevent premature sell-outs at lower rates.",
  metricLabel: "Strategy focus",
  metricValue: "Pace-based pricing & festive MLOS restrictions"
}, {
  id: 2,
  title: "Aggressive Comp-Set Undercutting",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Monitor intraday rate volatility across MakeMyTrip, Agoda, and Booking.com in the Mathura-Vrindavan corridor. Automated competitive alerts and yield adjustments protect your RevPAR against panic price wars.",
  metricLabel: "Strategy focus",
  metricValue: "Intraday OTA tracking & dynamic pricing fences"
}, {
  id: 3,
  title: "OTA Commission & Group Leakage",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Rebalance heavy reliance on high-commission OTAs and unvetted offline group bookings. We enforce strict rate parity across meta-search while optimizing direct pilgrimage packages and VIP family suites.",
  metricLabel: "Strategy focus",
  metricValue: "Direct booking engines & parity governance"
}];
const seasonalityPeriods = [{
  title: "Festive Super-Peaks",
  timing: "Janmashtami, Holi, Kartik Maas & Radhashtami",
  badge: "Maximum ADR Yield",
  icon: Sparkles,
  description: "Unprecedented demand influx where booking windows expand rapidly. Dynamic surge pricing, non-refundable deposit terms, and strict 2 to 3-night MLOS guarantee revenue maximization without inventory dilution.",
  metrics: "Target: 3.5x - 4x Base ADR"
}, {
  title: "Weekend Spiritual Rush",
  timing: "Friday to Sunday (Delhi-NCR & UP Drive-In)",
  badge: "Weekly Surge",
  icon: TrendingUp,
  description: "High-density weekend pilgrimage traffic from Delhi-NCR, Agra, and Haryana. Differentiated room categorisation and early-bird package fencing protect premium suite inventory for higher-yielding guests.",
  metrics: "Target: 100% Weekend Occupancy"
}, {
  title: "Mid-Week & Monsoons",
  timing: "Monday to Thursday & Shoulder Windows",
  badge: "Occupancy Stabilisation",
  icon: Clock,
  description: "Mitigate mid-week dips through targeted pilgrim family packages, senior citizen spiritual retreats, and corporate wellness stays without devaluing public brand rates on primary OTAs.",
  metrics: "Target: +25% Base Mid-Week RevPAR"
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapevrindavan" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Vrindavan
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Vrindavan presents a dynamic pilgrimage micro-market characterized by intense festival compression, sharp weekend influxes from Delhi-NCR, and volatile pilgrim demand cycles. Sunil Singh builds structured revenue architectures tailored specifically for pilgrimage hotels, boutique retreats, and spiritual resorts across the Braj circuit.
          </p>
        </div>

        {/* 3-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-chart-2" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-chart-2" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Vrindavan Seasonality & Yield Dynamics Block */}
        <div className="mt-14 md:mt-20 p-6 sm:p-8 lg:p-10 rounded-xl border border-border bg-card shadow-sm">
          <div className="max-w-3xl mb-8">
            <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-secondary font-semibold mb-2">
              <CalendarRange className="size-4" />
              <span>Braj Pilgrimage Calendar</span>
            </div>
            <h3 className="font-serif text-2xl sm:text-3xl font-bold text-foreground">
              Vrindavan Seasonality & Demand Cycles
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground mt-2 leading-relaxed">
              Unlike leisure beach or hill destinations, Vrindavan’s demand rhythm is dictated by the Hindu lunar calendar, festive occasions, and short-notice weekend travel from regional metro hubs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {seasonalityPeriods.map((period, i) => {
            const IconComp = period.icon;
            return <div key={period.title} data-index={i} className="rounded-lg border border-border/70 bg-muted/20 p-5 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div className="inline-flex size-9 items-center justify-center rounded-md bg-secondary/15 text-secondary">
                        <IconComp className="size-4.5" />
                      </div>
                      <Badge variant="secondary" className="text-[11px] font-medium bg-secondary/15 text-secondary-foreground">
                        {period.badge}
                      </Badge>
                    </div>
                    <h4 className="font-serif font-bold text-lg text-foreground mb-1">
                      {period.title}
                    </h4>
                    <p className="text-xs font-mono text-secondary-foreground/80 mb-3">
                      {period.timing}
                    </p>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {period.description}
                    </p>
                  </div>
                  <div className="mt-4 pt-3 border-t border-border/50">
                    <span className="text-xs font-semibold text-foreground">
                      {period.metrics}
                    </span>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Vrindavan hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}